
import _ from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useTableNavigation from '../../../../ui/tables/useTableNavigation';

import { ApplicableTableSettings, codesRegionUnLocationCodeList } from '../../../../tableSettings';

import {
  TextAlignLeftRenderer,
  TextRenderer,
} from '../../../../uiControls/SmartTableRenderers';

import SmartTable from '../../../../uiControls/SmartTable';

import useFastStorage, { useTypedFastStorage } from '../../../../hooks/useFastStorage';
import useFunction from '../../../../hooks/useFunction';
import useGlobalYesNoDialog from '../../../../hooks/useGlobalYesNoDialog';

import ApplicationContext from '../../../../ApplicationContext';
import SecondaryButton from '../../../../ui/SecondaryButton';
import { Search, isFilterObjectEmpty, resetSearchKeepPaging } from '../../../../filtering';
import { JobNames } from '../../../../framework.core/JobNames';
import { CodeUNLocationListItem } from '../../../../fms.data/models/CodeUNLocationListItem';
import { FmsSecTask } from '../../../../fms.core/constants';
import IfmsModal from '../../../../ui/IfmsModal';
import InlineInputControlRow from '../../../../ui/layout/InlineInputControlRow';
import TextBox from '../../../../ui/TextBox';
import useDirtyCheck from '../../../../hooks/useDirtyCheck2';
import { ApiResult } from '../../../../framework.core/data/ApiResult';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';
import LeftRightSplit from '../../../../ui/LeftRightSplit';
import IsoCountryDropDown from '../../../../uiControls/IsoCountryDropDown';
import { CodeOceanUnPort } from '../../../../fms.core/CodeOceanUnPort';
import { checkPropDirty, getSQLInfoList } from '../../../../fms.core/data/TrackingModule';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';
import { sortingListData } from '../../ReferenceFiles';
import { findSecurityTask } from '../../../../util';
import useFilterBar from '../../../../hooks/useFilterBar';
import { ListOrderBy } from "../../../common/AbstractListView";
import postal from 'postal';
import BlackInverseButton from '../../../../ui/BlackInverseButton';
import constants from '../../../../constants';
import IFmsReferenceFilesContext from '../../IfmsReferenceFilesContext';
import { Portal } from 'react-portal';
import CloseButton from '../../../../ui/CloseButton';
import { AddIconButton3, DeleteIconButton3, EditIconButton3, ViewIconButton3, RefreshIconButton2 } from '../../../../ui/IconButton';


function UnLocDetails({
  readonly,
  unLoc,
  onChange
}: {
  readonly: boolean,
  unLoc: CodeOceanUnPort,
  onChange: (change: Partial<CodeOceanUnPort>) => void
}) {

  useEffect(() => {
    if (readonly) {
      return
    };
    const focusElement = document.querySelector<HTMLInputElement>('input[name="unloc-unCountryCombo"]');
    focusElement?.focus();
  }, [readonly]);

  return (
    <div>
      <div style={{ width: '500px' }}>
        <InlineInputControlRow label={<span>UN Code <span style={{ color: 'red' }}>*</span></span>} mode="smaller-2x">
          <LeftRightSplit
            leftSize="60px"
            rightSize="calc(100% - 60px)"
            left={
              <IsoCountryDropDown
                maxLength={2}
                name='unloc-unCountryCombo'
                value={unLoc.unCountry}
                disabled={readonly}
                onChange={country => onChange({ unCountry: country?.value ?? '', unCountryName: country?.text ?? '', unCode: country?.value + unLoc.unCodeSub.toUpperCase() })}
              />
            }
            right={
              <div style={{ display: "flex" }}>

                <TextBox name='unloc-subcode' style={{ width: '100px' }} maxLength={3} disabled={readonly} value={unLoc.unCodeSub} onChange={e => onChange({ unCodeSub: e.target.value, unCode: unLoc.unCountry + e.target.value.toUpperCase() })} />
                <TextBox name='unloc-unCode' style={{ width: '150px' }} disabled value={unLoc.unCode} />
                <span>&nbsp;(Fixed Len: 5)</span>
              </div>
            }
          />
        </InlineInputControlRow>
      </div>
      <div style={{ width: '420px' }}>
        <InlineInputControlRow label="UN Location" mode="smaller-2x">
          <TextBox name='unloc-unPortName' maxLength={35} disabled={readonly} value={unLoc.unPortName} onChange={e => onChange({ unPortName: e.target.value })} />
        </InlineInputControlRow>
        <InlineInputControlRow label="Country" mode="smaller-2x">
          <LeftRightSplit
            leftSize="60px"
            rightSize="calc(100% - 60px)"
            left={
              <TextBox name='unloc-unCountry' disabled value={unLoc.unCountry} onChange={e => onChange({ unCountry: e.target.value })} />
            }
            right={
              <TextBox name='unloc-unCountryName' disabled value={unLoc.unCountryName} onChange={e => onChange({ unCountryName: e.target.value })} />
            }
          />
        </InlineInputControlRow>

        <InlineInputControlRow label="US Code" mode="smaller-2x">

          <TextBox name='unloc-usCode' maxLength={10} disabled={readonly} value={unLoc.usCode} onChange={e => onChange({ usCode: e.target.value })} />
        </InlineInputControlRow>
      </div>
    </div>
  );
}

function UnCodeRenderer({ data, row, cell }: any) {
  return <TextRenderer row={row} cell={cell} />;
}

function UnPortNameRenderer({ data, row, cell }: any) {
  return <TextAlignLeftRenderer row={row} cell={cell} />;
}

function UnCountryRenderer({ data, row, cell }: any) {
  return <TextRenderer row={row} cell={cell} />;
}

function UsCodeRenderer({ data, row, cell }: any) {
  return <TextRenderer row={row} cell={cell} />;
}

function formatData(data: any, pageNum: number, pageSize: number, pagePerBatch:number) {
  if (!data) return;
  
  const blockStartPage = pageNum - ((pageNum - 1) % pagePerBatch);
  
  return data.map((d: any, i: number) => {
    const indicator = (blockStartPage - 1) * pageSize + (i + 1);
    return { ...d, indicator };
  });
}

function UnLocationCode({
  componentIndex
}: {
  componentIndex: number
}) {
  const pageSize: number = 25;
  const pagePerBatch: number = 10;

  useFunction({
    invokers: {
      ifmsReferenceFilesSave: save,
      ifmsReferenceFilesCancel: cancelOperation,
    }
  });
  useFunctionKeys({ onEsc: cancelOperation });

  const appContext = useContext(ApplicationContext);
  const refContext = useContext(IFmsReferenceFilesContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();
  const { selectTableRow, tableNavigationCleanup } = useTableNavigation('codes-region-unloc-list', onRowSelect, selectUnLocForView, ['unCode'], false, false, false);
  const [pageNum, setPageNum, deletePageNum, pageNumRef] = useTypedFastStorage<number>(`codes-region-unloc-pageum`, 1);

  const [loading, setLoading] = useState(false);
  const [, setHasChanges] = useFastStorage(`ifms-referenceFiles-haschanges`, false);
  const [data, _setData, removeData, dataRef] = useFastStorage(`ifms-referenceFiles-region-unloc-list`, []);
  const [selectedRow, setSelectedRow] = useState(-1);

  const totalDataCountRef = useRef(0);
  const headerOrderByText = useRef('');

  const [unLocationCode, _setUnLocationCode, removeUnLocationCodeFromStorage, unLocationCodeRef] = useTypedFastStorage<CodeOceanUnPort>(`ifms-referenceFiles-unloc-editable`, null);
  const [orderBy, setOrderBy] = useTypedFastStorage<ListOrderBy>(`ifms-referenceFiles-region-unloc-orderby`, getDefaultOrderBy());

  const { setCleanState, dirtyCleanup } = useDirtyCheck(`ifms-referenceFiles-region-unloc-type-editable`, ['edit', 'view']);
  const listSec = findSecurityTask(appContext, FmsSecTask.CodeUNLocation);

  const tableHeight = 522;
  const listItemType = 'CodeUNLocationListItem';

  const { FilterButton, search } = useFilterBar(
    codesRegionUnLocationCodeList,
    undefined,
    getDefaultOrderBy(),
    String(componentIndex),
    false,
    setPageNum,
    {
      pageSize: pageSize,
      pagePerBatch: pagePerBatch,
    },
    setOrderBy,
  );

  function getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'unCode',
      orderByAscending: true,
    };
  }

  function setUnLocationCode(b: any, skipDirty: boolean = false) {
    if (!skipDirty) {
      setHasChanges(true);
    }
    _setUnLocationCode(b);
  }

  function setData(d: Array<any>) {
    removeData();
    _setData(d);
  }

  async function fetchData(api: any, pageNum: number, forceCallDb: boolean, newItem: any = undefined) {
    setLoading(true);

    const prevPageNum = pageNumRef.current;
    const prevBlockStartPage = prevPageNum ? prevPageNum - ((prevPageNum - 1) % search.pagePerBatch) : null;
    const prevBlockNum = prevBlockStartPage ? Math.floor((prevBlockStartPage - 1) / search.pagePerBatch) : null;

    setPageNum(pageNum);

    const blockStartPage = pageNum - ((pageNum - 1) % search.pagePerBatch);
    const blockNum = Math.floor((blockStartPage - 1) / search.pagePerBatch);

    const isSameBlock = prevBlockNum === blockNum;

    const searchWithHeaderOrder = {
      ...search,
      headerOrderBy: headerOrderByText.current,
    };

    let entities: any[] = [];

    if (!isSameBlock || forceCallDb) {
      const apiResult = await api.getListItems(JobNames.UNLocation,
        listItemType,
        CodeUNLocationListItem.calculateSearchParameters(searchWithHeaderOrder),
        blockStartPage,
        0,
        search.pageSize,
        search.pagePerBatch);

      entities = apiResult && apiResult.entities ? apiResult.entities : [];

      if (entities.length > 0) {
        if (newItem) {
          const filteredEntities = entities.filter(e => e.id !== newItem.id);

          let merged = [newItem, ...filteredEntities];

          const maxCount = search.pageSize * search.pagePerBatch;
          if (merged.length > maxCount) {
            merged = merged.slice(0, maxCount);
          }

          entities = merged;
        }
      }
      totalDataCountRef.current = apiResult?.totalCount ?? 0;
      setData(formatData(entities, pageNum, search.pageSize, search.pagePerBatch));
    }

    setLoading(false);
    setHasChanges(false);
  }

  useEffect(() => {
    if (isFilterObjectEmpty(search) && search.version === 1) reload();

    return () => {
      removeData();
      tableNavigationCleanup();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (loading || (isFilterObjectEmpty(search) && search.version === 1)) {
      return;
    }

    reload(true);
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search]
  );

  useEffect(() => {
    if (dataRef.current?.length > 0 && !loading && pageNum) {
      const blockStartPage = pageNum - ((pageNum - 1) % pagePerBatch);
      const indexOffset = (pageNum - blockStartPage) * pageSize;
      setTimeout(() => {
        selectTableRow(indexOffset, true);
        onRowSelect(indexOffset);
      }, 10);
    }
  }, [loading, pageNum]);

  function updateHeaderOrderByText(orderBy: string, orderByAscending: boolean) {
    if (!orderBy) return;
    const orderStr = orderByAscending
      ? `${orderBy} ASC`
      : `${orderBy} DESC`;
    headerOrderByText.current = orderStr;
  }

  async function reload(clearHeaderOrder: boolean = true) {
    if (clearHeaderOrder) {
      if (isFilterObjectEmpty(search)) {
        headerOrderByText.current = '';
        setOrderBy(getDefaultOrderBy());
      }
      else {
        updateHeaderOrderByText(search.orderBy, search.orderByAscending);
        setOrderBy({ orderBy: search.orderBy, orderByAscending: search.orderByAscending });
      }
    }

    await fetchData(appContext.api, 1, true);
  }

  function onRowSelect(rowIndex: number) {
    setSelectedRow(!isNaN(rowIndex) ? rowIndex : -1);
  }

  async function setUnLocDetail(isView: boolean, row: number = -1) {
    const tempRow = row > -1 ? row : selectedRow;  // row double click시 row 값에 rowindex가 들어옴. 이를 우선시 해서 사용함. 

    if (tempRow >= 0 && tempRow < dataRef.current.length && _.isEmpty(unLocationCode)) {
      const apiResult: ApiResult<CodeOceanUnPort> = await appContext.api.getJobDetail(JobNames.UNLocation,
        { id: dataRef.current[tempRow].id });
      if (!apiResult || !apiResult.success) {
        await showGlobalOkDialog(<p>{apiResult?.message ?? constants.LOAD_ERROR_MESSAGE}</p>);
        return null;
      } else {
        const detailData = { ...apiResult.result, edit: true, view: isView, editIndex: selectedRow };
        setCleanState(detailData);
        const tEntity = new CodeOceanUnPort(detailData);
        tEntity.trackable = true;
        setUnLocationCode(tEntity, true);
      }
    }
  }

  function selectForNew() {
    const newModel = new CodeOceanUnPort();
    newModel.edit = false;
    newModel.view = false;
    newModel.isNew = true;
    setUnLocationCode(newModel, false);
    setCleanState(newModel);

    if (unLocationCodeRef.current) {
      unLocationCodeRef.current.trackable = true;
      setUnLocationCode({
        edit: false,
        view: false,
      });
    }
  }

  async function selectUnLocForView(row: number) {
    await setUnLocDetail(true, row);
  }

  async function selectUnLocForEdit() {
    await setUnLocDetail(false);
  }

  async function selectUnLocForDelete() {
    const doConfirm = await showGlobalYesNoDialog(
      <div>
        <h3>Do you want to Delete?</h3>
      </div>
      , false, 0, true, 'warning');
    if (doConfirm) {
      const apiResult = await appContext.api.deleteJobDetail(JobNames.UNLocation, { id: dataRef.current[selectedRow].id, pimsLinkId: 0 });
      if (apiResult && apiResult.success) {
        await reload();
      }
      else
        showGlobalOkDialog(<p>{"Cannot delete"}</p>);
    }
    else
      return;
  }

  function cancelOperation() {
    const filterNode = document.getElementById('ifms-list-screen-filter');
    const channel = postal.channel('ifms-filter');

    if (!filterNode) {
      setHasChanges(false);
      removeUnLocationCodeFromStorage();
    } else {
      channel.publish('ifms-filter.dismiss');
    }
    return true;
  }

  async function checkDataAsync(): Promise<boolean> {
    if (!unLocationCode || !unLocationCodeRef.current) {
      return true;
    }

    const unl = unLocationCodeRef.current;
    if (!unLocationCodeRef?.current.unCode || unLocationCodeRef.current.unCode.length !== 5) {
      await showGlobalOkDialog(<p>Please fill out the [UnCode] (Fixed Length: 5)</p>);
      return false;
    } else if (unLocationCodeRef.current.unCode.length === 5 && checkPropDirty(unl, 'unCode')) {
      if (await appContext.api.selectUnPort(unLocationCodeRef.current.unCode, unLocationCodeRef.current.id)) {
        await showGlobalOkDialog(<p>Duplicate Number in [UNCode]</p>);
        return false;
      }
      return true;
    }
    return true;
  }

  async function deleteEntity() {
    if (!unLocationCode) {
      return;
    }
    if (unLocationCode.isNew) {
      cancelOperation();
      return;
    }

    const doDelete = await showGlobalYesNoDialog(<p>Are you sure you want to delete?</p>, false, 0, true, 'warning');
    if (!doDelete) {
      return;
    }
    const apiResult = await appContext.api.deleteJobDetail(JobNames.UNLocation, { id: unLocationCode.id });
    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(<p>{apiResult?.message ?? constants.SAVE_ERROR_MESSAGE}</p>);
      return;
    }
    cancelOperation();
    await reload();
  }

  async function save() {
    if (!unLocationCode || !pageNum) {
      return;
    }
    if (!unLocationCodeRef.current?.isDirty) {
      if (!unLocationCode.edit) {
        const isDataValid = await checkDataAsync();
        if (!isDataValid) {
          return;
        }
      }
      cancelOperation();
      reload();
      return;
    } else {
      const isDataValid = await checkDataAsync();
      if (!isDataValid) {
        return;
      }
      cancelOperation();
    }
    const sqlInfoList = getSQLInfoList(unLocationCode);
    const apiResult = await appContext.api.saveJobDetail(JobNames.UNLocation, sqlInfoList);

    if (apiResult && apiResult.success) {
      await showGlobalOkDialog(<p>The data has been saved</p>);

      if (apiResult.result) {
        const insresult = apiResult.result.insertList[0].newId;
        if (insresult) {
          const newItem: any = {
            id: insresult,
            unCode: unLocationCode.unCode,
            unCodeSub: unLocationCode.unCodeSub,
            unPortName: unLocationCode.unPortName,
            unCountry: unLocationCode.unCountry,
            unCountryName: unLocationCode.unCountryName,
            unCityName: unLocationCode.unCityName,
            subDiv: unLocationCode.subDiv,
            usCode: unLocationCode.usCode,
          };
          await fetchData(appContext.api, 1, true, newItem);
        }
      }
      else {
        const editItem = data.find((x: any) => x.id === unLocationCode.id);
        editItem.unCode = unLocationCode.unCode;
        editItem.unCodeSub = unLocationCode.unCodeSub;
        editItem.unPortName = unLocationCode.unPortName;
        editItem.unCountry = unLocationCode.unCountry;
        editItem.unCountryName = unLocationCode.unCountryName;
        editItem.unCityName = unLocationCode.unCityName;
        editItem.subDiv = unLocationCode.subDiv;
        editItem.usCode = unLocationCode.usCode;

        const insIndex = data.findIndex((x: any) => x.id === unLocationCode.id);
        const newList = _.cloneDeep(data);
        newList.splice(insIndex, 1, editItem);
        setData(formatData(newList, pageNum, pageSize, pagePerBatch));

        selectTableRow(insIndex);
      }

      cancelOperation();
      dirtyCleanup();
    }
  }

  function onHeaderClick(header: any) {
    if (!pageNum) return;

    if (!orderBy || !orderBy.orderBy) return;

    if (header && header.id !== 'indicator') {
      if (header.id === orderBy.orderBy) {
        setOrderBy({ orderByAscending: !orderBy.orderByAscending });
      } else {
        setOrderBy({ orderBy: header.id, orderByAscending: true });
      }

      let newOrderStr = `${header.id} ASC`;

      if (orderBy && orderBy.orderBy === header.id) {
        newOrderStr = orderBy.orderByAscending ? `${header.id} ASC` : `${header.id} DESC`;
      }

      headerOrderByText.current = newOrderStr;
      fetchData(appContext.api, pageNum, true);
    }
  }

  const tableOptions: ApplicableTableSettings = {
    customTableId: 'codes-region-unloc-list',
    selectable: true,
    tableName: 'regionUnLocationList',
    ...codesRegionUnLocationCodeList,
    height: `${tableHeight}px`,
    data: data.map((x: any, i: number) => ({ ...x })),
    rowRenderers: [
      { accessor: 'indicator', Cell: TextRenderer },
      { accessor: 'unCode', Cell: UnCodeRenderer },
      { accessor: 'unPortName', Cell: UnPortNameRenderer },
      { accessor: 'unCountry', Cell: UnCountryRenderer },
      { accessor: 'usCode', Cell: UsCodeRenderer },
      //{ accessor: 'action', Cell: ActionRenderer },
    ],
    orderBy: [orderBy],
    onHeaderClick,
    paging: {
      pageSize: pageSize,
      pageCount: Math.ceil(totalDataCountRef.current / pageSize),
      pagePerBatch: pagePerBatch,
      onPageSelect: (page: number) => {
        fetchData(appContext.api, page, false);
      },
      totalDataCount: totalDataCountRef.current,
      pageNum,
      labelStyle: {
        hide: false,
        isFilterEmpty: isFilterObjectEmpty(search),
        emptyLabel: 'Total',
      },
    },
  };

  const hasSelectedRow = !isNaN(selectedRow) && !(selectedRow < 0);
  const canBeAdd = listSec?.grantAdd;
  const canBeEdit = hasSelectedRow && listSec?.grantEdit;
  const canBeDeleted = hasSelectedRow && listSec?.grantDel;

  return (
    <div style={{ marginTop: '40px' }}>
      <Portal node={document && document.getElementById('settings-command-placeholder')}>
        <div style={{ display: 'flex', width: '100px' }}>
          <div style={{ margin: 'auto' }}></div>
          <div className="ifms-common-bar-element-center">
            <CloseButton onlyIcon name='settings-close' mode='light' onClick={refContext.cancel} />
          </div>
        </div>
      </Portal>

      <div style={{ display: 'flex', width: '830px' }}>
        <div style={{ marginLeft: 'auto' }}></div>
        <div style={{ display: 'flex', position: 'relative', top: '-4px' }}>
          <div style={{ display: 'none' }}>
            <FilterButton /> &nbsp;&nbsp;
          </div>
          <AddIconButton3 name='codes-unloc-add-button' disabled={!canBeAdd} onClick={selectForNew} />&nbsp;&nbsp;
          <EditIconButton3 name='codes-unloc-edit-button' disabled={!canBeEdit} onClick={selectUnLocForEdit} />&nbsp;&nbsp;
          <ViewIconButton3 name='codes-unloc-view-button' disabled={!hasSelectedRow} onClick={selectUnLocForView} iconHeight='26px' />&nbsp;&nbsp;
          <DeleteIconButton3 name='codes-unloc-delete-button' disabled={!canBeDeleted} onClick={selectUnLocForDelete} />&nbsp;
          {/* <BlackInverseButton name='list-screen-refresh' disabled={loading} size="large" onClick={() => { cancelOperation(); reload(); }} data-tooltip="Refresh" >
            <FontAwesomeIcon color='#47C83E' icon={faSync} />
          </BlackInverseButton> */}
          <RefreshIconButton2 name='list-screen-refresh' onClick={() => { cancelOperation(); reload(); }} disabled={loading} data-tooltip="Refresh"/>
        </div>
      </div>

      <div style={{ width: '830px' }}>
        <SmartTable {...tableOptions} />
      </div>
      <IfmsModal
        width="760px"
        isBlueHeader
        isOpen={!!unLocationCode}
        title={unLocationCode && unLocationCode.edit ? (unLocationCode.view ? 'UN Location Code View' : 'UN Location Code Edit') : 'UN Location Code New'}
        onCancel={cancelOperation}
      >
        {!!unLocationCode &&
          <div className="report-popup">
            <UnLocDetails readonly={unLocationCode.view || !canBeEdit} unLoc={unLocationCode} onChange={setUnLocationCode} />
            <div className='report-footer'>
              {canBeEdit && !unLocationCode.view &&
                <SecondaryButton
                  name='unloc-detail-save-button'
                  size="regular"
                  style={{ width: '90px' }}
                  onClick={save}
                >
                  SAVE
                </SecondaryButton>
              }
              {canBeEdit && unLocationCode.view &&
                <SecondaryButton
                  name='unloc-detail-edit-button'
                  size="regular"
                  style={{ width: '90px' }}
                  onClick={() => setUnLocationCode({ edit: true, view: false })}
                >
                  Edit
                </SecondaryButton>
              }

              {!unLocationCode.isNew && !unLocationCode.view && listSec?.grantDel &&
                <SecondaryButton
                  size="regular"
                  name='unloc-detail-delete-button'
                  style={{ width: '90px' }}
                  onClick={deleteEntity}
                >
                  DELETE
                </SecondaryButton>
              }
              <SecondaryButton
                name='unloc-detail-close-button'
                size="regular"
                style={{ width: '90px' }}
                onClick={cancelOperation}>
                CANCEL
              </SecondaryButton>
            </div>
          </div>
        }
      </IfmsModal>
    </div>
  );
}

export default UnLocationCode;
